// src/CustomCalendar.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { addMonths, format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isPast, isWithinInterval, isEqual } from 'date-fns';

const CalendarContainer = styled.div`
  display: flex;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 20px;
  width: 600px; /* Adjusted width for a larger calendar */
`;

const MonthContainer = styled.div`
  margin: 0 20px;
`;

const MonthHeader = styled.div`
  text-align: center;
  font-size: 1.5rem; /* Increased font size */
  font-weight: bold;
  margin-bottom: 10px;
`;

const WeekdaysContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 1rem; /* Increased font size */
`;

const DaysContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Day = styled.div`
  width: 40px; /* Ensure height and width are equal */
  height: 40px; /* Ensure height and width are equal */
  line-height: 40px; /* Adjusted line height */
  text-align: center;
  cursor: pointer;
  color: ${props => (props.isPast || !props.isSameMonth ? '#ccc' : '#000')};
  background-color: ${props => (props.isSelected ? '#007bff' : props.isInRange ? '#e6f7ff' : 'transparent')};
  border-radius: 50%; /* Ensure border radius is 50% for a round shape */
  &:hover {
    background-color: ${props => (!props.isPast && props.isSameMonth ? '#e6f7ff' : 'transparent')};
  }
`;

const CustomCalendar = ({ onDateSelect, selectedDates, setSelectedDates }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const handleDayClick = (date) => {
    if (isPast(date) && !isToday(date)) return;
    let newDates = { ...selectedDates };

    if (!newDates.start || (newDates.start && newDates.end)) {
      newDates = { start: date, end: null };
    } else if (newDates.start && !newDates.end) {
      if (date < newDates.start) {
        newDates = { start: date, end: newDates.start };
      } else {
        newDates.end = date;
      }
    }

    setSelectedDates(newDates);

    if (newDates.start && newDates.end) {
      onDateSelect(newDates);
    }
  };

  const renderMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    const days = eachDayOfInterval({ start, end });

    return (
      <MonthContainer key={date}>
        <MonthHeader>{format(date, 'MMMM yyyy')}</MonthHeader>
        <WeekdaysContainer>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{ width: 'calc(100% / 7)', textAlign: 'center' }}>{day}</div>
          ))}
        </WeekdaysContainer>
        <DaysContainer>
          {days.map(day => (
            <Day
              key={day}
              onClick={() => handleDayClick(day)}
              isPast={isPast(day) && !isToday(day)}
              isSameMonth={isSameMonth(day, date)}
              isSelected={isEqual(day, selectedDates.start) || isEqual(day, selectedDates.end)}
              isInRange={selectedDates.start && selectedDates.end && isWithinInterval(day, { start: selectedDates.start, end: selectedDates.end })}
            >
              {format(day, 'd')}
            </Day>
          ))}
        </DaysContainer>
      </MonthContainer>
    );
  };

  return (
    <CalendarContainer>
      {renderMonth(currentMonth)}
      {renderMonth(addMonths(currentMonth, 1))}
    </CalendarContainer>
  );
};

export default CustomCalendar;
